import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Typography, Tooltip, TablePagination, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { formatDate } from '../../../utils/helpers';
import adminService from '../../../services/adminService';
import AdminToolbar from '../components/AdminToolbar';

const OrderManagement = () => {
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    // Delete confirmation state
    const [deleteId, setDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        setPage(0);
    }, [search, filterStatus]);

    useEffect(() => {
        fetchOrders();
    }, [search, filterStatus, page, rowsPerPage]);

    const fetchOrders = async () => {
        const params = {
            page: page + 1,
            limit: rowsPerPage
        };
        if (search) params.search = search;
        if (filterStatus !== 'all') params.status = filterStatus;

        const res = await adminService.getAllOrders(params);
        if (res.success) {
            setOrders(res.data);
            setTotalRows(res.total || 0);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
    };

    const handleConfirmDelete = async () => {
        if (!deleteId) return;
        setIsDeleting(true);
        try {
            const res = await adminService.deleteOrder(deleteId);
            if (res.success) {
                toast.success(res.message || 'Đã xóa đơn hàng thành công');
                fetchOrders();
            } else {
                toast.error(res.message || 'Lỗi khi xóa đơn hàng');
            }
        } catch (err) {
            toast.error('Lỗi kết nối máy chủ');
        } finally {
            setIsDeleting(false);
            setDeleteId(null);
        }
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 3 }}>Quản Lý Đơn Hàng</Typography>

            <AdminToolbar
                searchValue={search}
                onSearchChange={setSearch}
                filterValue={filterStatus}
                onFilterChange={setFilterStatus}
                filterOptions={[
                    { value: 'all', label: 'Tất cả trạng thái' },
                    { value: 'PENDING', label: 'Đang xử lý' },
                    { value: 'COMPLETED', label: 'Hoàn thành' },
                    { value: 'CANCELLED', label: 'Đã hủy' },
                    { value: 'FAILED', label: 'Thất bại' }
                ]}
                placeholder="Tìm user hoặc mã đơn..."
            />

            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Mã Đơn</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Khách Hàng</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Dịch Vụ</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Thanh Toán (VND)</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Phương Thức</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Ngày Mua</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Trạng Thái</TableCell>
                            <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Thao Tác</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map((order) => (
                            <TableRow key={order.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                <TableCell sx={{ color: 'white' }}>
                                    <Tooltip title={order.id} arrow>
                                        <Typography fontFamily="monospace" variant="body2" sx={{ fontWeight: 'bold', color: '#60a5fa', cursor: 'pointer' }}>
                                            #{order.id.substring(0, 8).toUpperCase()}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    <Typography fontWeight="bold">{order.user?.username || 'Unknown'}</Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{order.user?.email}</Typography>
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    {order.snapshotName}
                                </TableCell>
                                <TableCell sx={{ color: '#4ade80', fontWeight: 'bold' }}>
                                    {Number(order.finalAmount).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    {order.paymentMethod}
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    {formatDate(order.createdAt, 'dd/MM/yyyy HH:mm:ss')}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={order.status}
                                        color={order.status === 'COMPLETED' ? 'success' : 'warning'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Xóa mềm (Lưu trữ)">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleDeleteClick(order.id)}
                                            sx={{ 
                                                color: '#ef4444', 
                                                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)', transform: 'scale(1.1)' },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ color: 'white' }}
            />

            {/* Confirmation Dialog */}
            <Dialog 
                open={Boolean(deleteId)} 
                onClose={() => setDeleteId(null)}
                PaperProps={{
                    sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    Xác nhận xóa đơn hàng
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography>
                        Bạn có chắc chắn muốn xóa đơn hàng này không? 
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                        Lưu ý: Đơn hàng sẽ bị ẩn khỏi danh sách chính nhưng vẫn được lưu trong cơ sở dữ liệu để đối soát.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Button onClick={() => setDeleteId(null)} sx={{ color: '#94a3b8' }}>Hủy</Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        variant="contained" 
                        color="error"
                        disabled={isDeleting}
                        sx={{ borderRadius: 2, fontWeight: 'bold' }}
                    >
                        {isDeleting ? 'Đang xóa...' : 'Đồng ý xóa'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrderManagement;
